import THREE from "realms-three";
import {
  GameObject,
  Scene,
  TimerGameObject,
  TweenGameObject,
} from "realms-engine";
import {
  BASE_EMISSIVE_COLOR,
  HIT_FLASH_COLOR,
  HIT_FLASH_DURATION_MS,
} from "../config/combat";
import { setEmissive } from "./setEmissive";
import { forEachMaterial } from "./forEachMaterial";

export interface ConfigurableMeshEventsMap<T extends {}> {
  onMeshChange: ConfigurableMesh<T>;
}

export type ConfigurableMeshChanges<T extends {}> = {
  [k in keyof T]?: {
    oldValue: T[k];
    newValue: T[k];
  };
};

export interface ConfigurableMeshOptions {
  scale?: number;
  opacityTweenTimeMS?: number;
}

/**
 * A mesh that can be configured
 */
export abstract class ConfigurableMesh<T extends {}> extends GameObject<
  ConfigurableMeshEventsMap<T>
> {
  private currentConfig: T;
  protected rootGroup: THREE.Object3D;
  private emissive = new THREE.Color().setHex(0x000000);
  private onHitFlashEndTimer: TimerGameObject;
  private opacityTween: TweenGameObject<number>;
  private opacity = 0;
  private opacityTweenTimeMS: number;

  constructor(
    protected baseMesh: THREE.Object3D,
    config: T,
    options: ConfigurableMeshOptions = {}
  ) {
    super();
    this.rootGroup = new THREE.Object3D();
    this.rootGroup.scale.setScalar(options.scale ?? 1);
    this.currentConfig = config;
    this.updateConfig(config, { ignoreCache: true });

    this.onHitFlashEndTimer = this.addChild(new TimerGameObject());
    this.onHitFlashEndTimer.addListener("onFinish", () => {
      this.setEmissive(new THREE.Color(BASE_EMISSIVE_COLOR));
    });

    this.opacityTween = this.addChild(
      new TweenGameObject(
        () => this.getOpacity(),
        (v) => this.setOpacity(v)
      )
    );
    this.setOpacity(0);
    this.opacityTweenTimeMS = options.opacityTweenTimeMS ?? 1000;
  }

  tweenOpacity(opacity: number) {
    this.opacityTween.tween(opacity, this.opacityTweenTimeMS);
  }

  onInit(scene: Scene): void {
    this.tweenOpacity(1);
    super.onInit(scene);
  }

  private getOpacity() {
    return this.opacity;
  }

  private setOpacity(opacity: number) {
    this.opacity = opacity;
    forEachMaterial(this.baseMesh, (material) => {
      material.opacity = opacity;
      material.transparent = true;
    });
  }

  getEmissive() {
    return this.emissive;
  }

  playHitFlash() {
    this.setEmissive(HIT_FLASH_COLOR);
    this.onHitFlashEndTimer.start(HIT_FLASH_DURATION_MS);
  }

  /**
   * Set emmisive color
   */
  setEmissive(color: THREE.Color) {
    this.emissive = color;
    setEmissive(this.rootGroup, color);
  }

  /**
   * Updates the mesh config
   */
  updateConfig(config: T, options: { ignoreCache?: boolean } = {}) {
    const changes: ConfigurableMeshChanges<T> = {};
    for (const [k, v] of Object.entries(this.currentConfig)) {
      if (config[k as keyof T] !== v) {
        changes[k as keyof T] = {
          oldValue: v as T[keyof T],
          newValue: config[k as keyof T],
        };
      }
    }

    // No changes so ignore
    if (Object.entries(changes).length === 0 && !options.ignoreCache) {
      return;
    }

    // Update mesh changes

    const oldMesh = this.baseMesh;

    const newMesh = this.onConfigChanges(config, changes);
    if (oldMesh !== newMesh || options.ignoreCache) {
      this.rootGroup.remove(oldMesh);
      this.baseMesh = newMesh;
      this.rootGroup.add(newMesh);
      this.dispatch("onMeshChange", this);
      this.currentConfig = config;
    }
  }

  /**
   * Returns the root group
   */
  getObject() {
    return this.rootGroup;
  }

  /**
   * Updates the mesh on changes
   */
  protected abstract onConfigChanges(
    config: T,
    changes: ConfigurableMeshChanges<T>
  ): THREE.Object3D;
}
